export default {
  preprocessingLoader: {
    title: 'Checking geometry',
    message:
      'Health checks run before meshing. This usually finishes in a minute.',
  },
  sectionShell: {
    noData: 'No data yet',
  },
  studyHeader: {
    backToStudies: 'Back to studies',
    backToEvaluations: 'Back to evaluations',
    studyNameLabel: 'Study name',
    renameTooltip: 'Click to rename study',
    renameFailed: 'Rename failed',
    saving: 'Saving...',
    saveFailed: 'Save failed',
    lastSaved: 'Last saved {{time}}',
    dpsReady: '{{done}} of {{total}} DPs ready',
    // Analyze-route merged header (ENG-1842). Reuses
    // `simopsAnalysis.workbench.exportPdf*` for the Export PDF button so
    // both the header and the (soon-retired) rail read one copy.
    modeOverview: 'Overview',
    modePoints: 'Design Points',
    modeCompare: 'Compare',
    modeLabel: 'Results view',
    // Display density (gear beside the mode tabs). The hints say what each
    // level costs and buys, because the names alone do not: compact is worth
    // choosing for a 24-point sweep and not for a 4-point one.
    densityTrigger: 'Display density',
    densityLabel: 'Display density',
    density_comfortable: 'Comfortable',
    densityHint_comfortable: 'Each panel its own card, with space between',
    density_compact: 'Compact',
    densityHint_compact: 'One ruled sheet — fits the most on screen',
    phaseMesh: 'Mesh',
    phaseSimulate: 'Simulate',
    phaseAnalyze: 'Results',
    journeyLabel: 'Study progress',
    writeup: 'Writeup',
  },
  studyKind: {
    heading: 'Study method',
    changeTooltip: 'Change study method',
    fixedTooltip:
      'Method is fixed once the study has started — clone to try another',
    comingSoon: 'Coming soon',
    changeError: "Couldn't change method. Try again.",
    options: {
      doe: {
        label: 'DOE',
        subtitle: 'Run every combination',
      },
      surrogate: {
        label: 'Surrogate',
        subtitle: 'Train a fast predictor',
      },
      gci: {
        label: 'GCI',
        subtitle: 'Mesh independence study',
      },
    },
  },
  solver: {
    incompressible: 'OpenFOAM — {{flow}} Incompressible',
    unknownFlow: 'Unknown',
    kinematicViscosity: 'Kinematic viscosity',
    inletVelocity: 'Inlet velocity',
    outletPressure: 'Outlet pressure',
    turbulenceIntensity: 'Turbulence intensity',
    turbulentLengthScale: 'Turbulent length scale',
    convergenceCriteria: 'Convergence Criteria',
    solverSettings: 'Solver Settings',
    advancedFold: 'Show advanced settings',
    default: 'default',
    inletBoundaryConditions: 'Inlet & Boundary Conditions',
    axisInlet: '{{axis}} inlet',
    axisLabel: '{{axis}}-axis',
    bc: {
      velocityInlet: 'Velocity inlet',
      pressureOutlet: 'Pressure outlet',
      wallNoSlip: 'Wall (no-slip)',
      slip: 'Slip',
      symmetry: 'Symmetry',
      freestream: 'Freestream',
    },
  },
  cht: {
    roleFluid: 'Fluid',
    roleSolid: 'Solid',
    inletTemperature: 'Inlet temperature',
    regions: 'Regions ({{count}})',
    boundaryConditions: 'Boundary Conditions ({{count}})',
    bcFixedTemperature: 'fixed T = {{value}} K',
    bcConvection: 'convection: h = {{h}} W/(m²·K), T∞ = {{tAmbient}} K',
    bcHeatSource: 'volumetric source: {{value}} W/m³',
    bcHeatFlux: 'heat flux: {{flux}} W/m²',
    regionHeatSource: 'heat source',
    // Temperature-dependent property panel (ENG-2002). Lives in the SHARED
    // namespace because the authoring panel, the Overview ranking and the
    // report all render the same chart component and must not each own a
    // copy of this vocabulary.
    //
    // Property NAMES are not repeated here — `simopsSolver.cht.density`,
    // `.kinematicViscosity`, `.specificHeat` and `.conductivity` already
    // exist and are what the pickers show. A second set would drift.
    propertyCurves: {
      modeConstant: 'Constant',
      // "Curve", not "Follows T" or "Varies with temp.": the segmented control
      // sits at the right end of a label row already carrying an uppercased
      // property name, an info icon and the import button, so at 10px in a
      // 360px sidebar a phrase wraps (measured: "Follows T" already wrapped on
      // the viscosity row). Matches REC. CURVE and the curve button — the word
      // names the DATA here, while `modeAria` carries the behaviour for
      // screen readers.
      modeCurve: 'Curve',
      modeAria: 'How {{property}} varies with temperature',
      // Compact chips for the case sheet and the report, where a column is
      // a few characters wide. Capitalized because they read as tags, not
      // sentences.
      // The case sheet's own column headers. Kept in this group rather than
      // borrowed from `responseKeys`, which labels result keys — one word
      // serving two label spaces is how they drift apart. Its region tags
      // reuse `simopsShared.cht.roleFluid` / `roleSolid`, upper-cased by CSS.
      caseSheetTitle: 'Material definition',
      caseSheetValue: 'Value / range',
      caseSheetMode: 'Mode',
      chipCurve: 'CURVE',
      chipConstant: 'CONST',
      recommendCurve: 'REC. CURVE',
      recommendConstant: 'REC. CONST',
      chartDescription:
        'Runs from {{low}} to {{high}} {{unit}} between {{lowTemperature}} and {{highTemperature}} °C.',
      inspectorTitle: '{{property}} vs temperature',
      inspectorClose: 'Close curve inspector',
      expectedRange: 'Expected range',
      expectedRangeLow: 'Lowest temperature',
      expectedRangeHigh: 'Highest temperature',
      // "below" was true while the ranking was an inline collapsible under
      // this field. It is an overlay beside the panel now.
      expectedRangeHint:
        'Drives which properties are recommended as curves — see Overview.',
      expectedRangeDrag: 'Drag the upper bound',
      expectedRangeMinSpan:
        'Raised to keep at least {{span}} °C between the two ends.',
      clamped: 'Clamped to table',
      // "This table", not "the source data": the limit belongs to the STORED
      // table, not the fluid. PG25 reads to 100 °C, but a table sampled over
      // 20-35 °C says 35, and the old wording made that read as the fluid's
      // ceiling (ENG-2179).
      //
      // Origin-neutral on purpose. The report renders these keys too, for a
      // finished run, and an imported table was never sampled at all — so
      // neither "sampled over" nor "re-sample to go higher" can appear here.
      //
      // Two messages, not one with a fixed direction: the marker clamps at
      // BOTH ends, and a table starting at 27 °C with an expected range below
      // it was being told the data "stops above" that point.
      clampedTipHigh:
        'This table stops at {{limit}} °C. Above that the solver holds the last value rather than continuing the trend, and the run does the same.',
      clampedTipLow:
        'This table starts at {{limit}} °C. Below that the solver holds the first value rather than continuing the trend, and the run does the same.',
      sensitivity: 'Sensitivity',
      sensitivityHint:
        'How far the property moves across the expected range, against its smaller end. Over {{threshold}} % is worth following rather than pinning.',
      sensitivityUnavailable: 'Not measurable',
      accuracy: 'Straight-line reading is within {{percent}} % of the source',
      source: 'Source: {{source}}',
      unverified: 'Unverified',
      unverifiedTip:
        'These numbers were transcribed from the cited source but not second-checked. The mark is stored with the study and appears in the report.',
      constantsOnly: 'Constants only',
      noCurveReason: 'No curve: {{reason}}',
      // "No sample yet" only — a fluid that HAS curves but has none in hand:
      // in flight, or the library list not read. A fluid with no curves at all
      // says `constantsOnly`, a refused window `curveWindowRefused`, and a
      // request that FAILED (503 without CoolProp, network) `curveFailed` —
      // telling someone to wait for a sample that is not coming is the same
      // defect this key was split to fix.
      curveUnavailable: 'No curve sample yet',
      curveFailed: 'Curves are unavailable right now',
      // Short on purpose. The refusal banner above the properties already
      // prints the backend's whole sentence; repeating it in a padlock
      // tooltip on all four rows says the same long thing five times.
      curveWindowRefused: 'Window outside the data',
      // ENG-3082 / ENG-3070: constant props pinned at reference_temperature_k
      // while BC temps sit far away (air bake at 110 °C vs 20 °C library).
      constantFarFromReference:
        "Boundary temperatures are about {{delta}} K from this fluid's {{refC}} °C reference, but one or more properties are still Constant. Switch them to Curve so the library follows temperature.",
      curveGridLocked:
        'This region already has a curve on {{existing}}, and the sample for this property runs {{offered}}. Every curve in one region has to share one temperature grid — the solver reads density times viscosity point by point — so this one cannot join it. Set the imported curve back to Constant first, or re-import it on these temperatures.',
      overview: 'Overview',
      overviewTitle: 'What varies in this case',
      overviewClose: 'Close overview',
      rankingTitle: 'Temperature sensitivity',
      rankingEmpty: 'No curve data for this material.',
      rankingProperty: 'Property',
      rankingRange: '{{low}} °C → {{high}} °C',
      // Report-only: where the authoring panel draws a marker, the report
      // draws the range the run actually reached.
      excursion: 'It went {{kelvin}} K past that edge.',
      // Measured-curve import (ENG-2020).
      importTitle: 'Import measured {{property}}',
      importOpen: 'Import measured data for {{property}}',
      importReplace: 'Replace {{property}} with measured data',
      // Deliberately short: the worked example below the paste box shows the
      // shape, and the syntax list beside it states the rules. This sentence
      // only has to say what the two columns ARE.
      importHint:
        'A two-column CSV: temperature, then the value. Check it here before it goes on the case.',
      // Heading over the always-visible example. It replaces the textarea
      // placeholder, which vanished on the first keystroke and was never seen
      // at all by anyone using the file picker (ENG-2179).
      importExample: 'What the file should look like',
      // Names the unit rather than saying "in SI". The two differ for
      // viscosity — the chart is mm²/s and the file wants m²/s — and "SI" left
      // the reader to work out which one that was.
      importExampleUnit:
        'Temperature in the unit chosen below; {{property}} in {{unit}}.',
      // The rules a reader can act on while writing the file. NOT every
      // check the parser runs — it also caps size and row count, requires
      // 0.5 K spacing and refuses duplicate temperatures. Those are verdicts
      // on data already typed; these five are what shape the file.
      //
      // Semicolons are REFUSED, not auto-handled, so the old hint's silence
      // on them was the expensive kind. The point floor is here because the
      // downloadable template is built to satisfy it (ENG-2179).
      importRuleColumns: 'Exactly two columns, comma-separated.',
      importRuleSemicolon:
        'Semicolons are refused — re-export with commas and a decimal point.',
      importRuleHeading:
        'One heading row, as the first line that is not a # comment. A second non-numeric line is an error.',
      importRulePoints: 'At least five rows, so the accuracy check can run.',
      importRuleSkipped: 'Blank lines and lines starting with # are skipped.',
      importRuleExcel: 'Excel’s “CSV UTF-8” export is fine.',
      importTemplate: 'Download template',
      importFile: 'Choose a CSV file',
      importPaste: 'or paste the rows',
      importUnit: 'Temperature column',
      importLabel: 'Call this data',
      importLabelPlaceholder: 'e.g. ACME 40/60, datasheet rev C',
      importLabelHint:
        'Becomes the curve source, with today’s date. It is the only provenance an imported curve has, and it is printed in the report.',
      importCheck: 'Check the file',
      importAccepted: '{{rows}} rows accepted.',
      importReordered:
        'Rows were out of temperature order and have been sorted.',
      importGridClash:
        'The other curves on this region run {{existing}}, and this file runs {{incoming}}. Every curve in one region has to share one temperature grid — the solver reads density times viscosity point by point — so this file cannot join them. Set the others back to Constant first, or re-export this one on the same temperatures.',
      importChecking: 'Checking the file…',
      importAccuracy:
        'Reading between your points misses the curve they imply by up to {{percent}} %',
      importTooBig:
        'That file is {{chars}} characters, over the {{limit}} this reader takes. A property curve is a few dozen rows; a file this size is usually a whole spreadsheet export.',
      importUseConstant: 'Use this value',
      importIsConstant:
        'One value, no temperature — this will be stored as a constant.',
      importTooFewRows:
        'A curve needs at least {{min}} rows — below that the accuracy check reports 0 % on curves that are really several per cent out. A constant needs exactly 1 value. This file has {{rows}}.',
      importNotPositive:
        'A property value must be greater than 0. This file has {{value}}.',
      importConstantHasTemperature:
        'A single-row file must hold just the value, with no temperature. A constant applies at every temperature, so there is nowhere to put one.',
      importUse: 'Use this curve',
      importCancel: 'Discard',
      importFailed:
        'The file could not be processed. That is on us, not on your data — try again.',
      importReadFailed: 'That file could not be read from disk.',
      solvedBand: 'Solved range',
      solvedBandTip:
        'The temperatures this run actually reached, {{low}} - {{high}} °C. Where the authoring panel showed the range you expected, this shows what happened.',
    },
  },
} as const;
